/**
 * GraphQL queries and hooks — userPreference pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import type { UserPreferences, AvailableWidget } from '../../types/preferences';

/**
 * Query for the authenticated user's preferences (issue #424). Read path is
 * GraphQL; the save path is the REST `updateUserPreferences` mutation.
 */
export const GET_USER_PREFERENCES = gql`
  query GetUserPreferences {
    userPreferences {
      userId
      theme
      locale
      timezone
      dashboardLayout
    }
  }
`;

/**
 * Query for the role-filtered dashboard widget catalog (issue #449/#450), used
 * to populate the "Add widget" picker in the dashboard's Configure edit mode.
 */
export const GET_AVAILABLE_WIDGETS = gql`
  query GetAvailableWidgets {
    availableWidgets {
      id
      title
      minRole
      defaultSize
    }
  }
`;

/**
 * Hook to load the authenticated user's preferences (issue #424).
 */
export function useUserPreferences(options?: {
  skip?: boolean;
}): QueryResult<{ userPreferences: UserPreferences | null }> {
  return useQuery(GET_USER_PREFERENCES, {
    skip: options?.skip,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Hook to load the role-filtered dashboard widget catalog (issue #449/#450).
 */
export function useAvailableWidgets(options?: {
  skip?: boolean;
}): QueryResult<{ availableWidgets: AvailableWidget[] }> {
  return useQuery(GET_AVAILABLE_WIDGETS, {
    skip: options?.skip,
    fetchPolicy: 'cache-first',
  });
}
